<template>
  <div class="resume-picker">
    <template v-if="!confirmed">
      <button type="button" class="btn btn-link px-0 mb-3 text-decoration-none" @click="$emit('close')">
        <span class="material-icons align-middle">chevron_left</span> {{ $t('shared.back') }}
      </button>

      <h1 class="h3 mb-1"><b>{{ $t('purchases.resume_picker.resume_your_subscription') }}</b></h1>
      <p class="text-muted mb-4">{{ $t('purchases.resume_picker.pick_up_your_pro_subscription_where') }}</p>

      <div class="card resume-picker__body">
        <div class="card-body d-flex align-items-center gap-3">
          <span class="material-icons resume-picker__icon">play_circle</span>
          <p class="mb-0">{{ explanation }}</p>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger mt-3">{{ error }}</div>

      <button type="button" class="btn btn-primary w-100 mt-4" :disabled="submitting" @click="resume">
        <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
        {{ $t('purchases.resume_picker.resume_my_subscription') }}
      </button>
    </template>

    <FlowConfirmation
      v-else
      :title="resumeConfirmedTitle()"
      :body-prefix="resumeConfirmedBody()"
      @continue="$emit('done')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import graphClient from "@/utils/graphql/graph_client";
import { ResumeWebSubscription } from "@/utils/graphql/subscriptions";
import { errorMessage } from "../errorMessage";
import {
  resumeConfirmedBody,
  resumeConfirmedTitle,
  resumeExplanation,
} from "../subscriptionLogic";
import FlowConfirmation from "./FlowConfirmation.vue";

// Both come off the display model, which derives them from the backend's pause
// dates — resuming mid-gap charges the saved card, resuming before then doesn't.
const props = defineProps<{ chargesImmediately: boolean; accessEndsOn: string | null }>();
defineEmits<{ (e: "close"): void; (e: "done"): void }>();

const submitting = ref(false);
const error = ref("");
const confirmed = ref(false);

const explanation = computed(() => resumeExplanation(props.chargesImmediately, props.accessEndsOn));

async function resume() {
  if (submitting.value) return;
  submitting.value = true;
  error.value = "";
  try {
    await graphClient.request(ResumeWebSubscription, { input: {} });
    confirmed.value = true;
  } catch (e) {
    error.value = errorMessage(e);
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped lang="scss">
.resume-picker {
  &__body {
    border-radius: 16px;
  }

  &__icon {
    color: #caa300;
    font-size: 2rem;
  }
}
</style>
